import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import './CategoryRail.css';
import LoadingSpinner from './common/LoadingSpinner';
import { HorizontalRailSkeleton, ChannelCardSkeleton } from './common/SkeletonLoaders';
import ChannelCard from './ChannelCard';
import ThumbnailHoverModal from './ThumbnailHoverModal';
import PDPModal from './PDPModal';
import { useCategoryRail } from '../hooks/useCategoryRail';
import { getSlidesToShow, RAIL_BREAKPOINTS } from '../config/categoryRailSettings';

const CategoryRail = React.memo(({ 
  category,
  preCalculatedContinueWatching = [],
  preCalculatedWatchAgain = []
}) => {
  const [isVisible, setIsVisible] = useState(false);
  const [hasBeenVisible, setHasBeenVisible] = useState(false);
  const [hoveredCardIndex, setHoveredCardIndex] = useState(-1);

  const [pdpModalData, setPdpModalData] = useState({
    isVisible: false,
    channel: null,
    videoState: { autoPlay: false, currentTime: 0 },
  });

  const railRef = useRef(null);
  const navigate = useNavigate();

  // Local modal state management - no global context needed
  const [localModalState, setLocalModalState] = useState({
    isVisible: false,
    channel: null,
    position: null,
    index: -1,
  });

  // Local timeout management
  const hideTimeoutRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasBeenVisible) {
          setIsVisible(true);
          setHasBeenVisible(true); // Once loaded, keep it loaded
        }
      },
      {
        root: null,
        rootMargin: '200px', // Start loading earlier
        threshold: 0.1,
      },
    );

    if (railRef.current) {
      const currentRef = railRef.current;
      observer.observe(currentRef);
      return () => observer.unobserve(currentRef);
    }
  }, [hasBeenVisible]);

  // Use the new custom hook for category channels data fetching
  const { channels, loading, error, retry } = useCategoryRail(category, isVisible);

  // Add neighbor displacement functions first (before they're used)
  const applyNeighborDisplacement = useCallback(hoveredIndex => {
    if (!railRef.current) return;

    // Get all cards in the rail
    const sliderTrack = railRef.current.querySelector('.slick-track');
    if (!sliderTrack) return;

    const cards = sliderTrack.querySelectorAll('.channel-card-container');

    cards.forEach((card, index) => {
      if (index === hoveredIndex) return;

      const distance = index - hoveredIndex;
      const direction = distance > 0 ? 1 : -1;
      const absDistance = Math.abs(distance);

      let displacement = 0;

      // Cards immediately adjacent get pushed more
      if (absDistance === 1) {
        displacement = direction * 60; // 60px displacement
      }
      // Cards two positions away get pushed less
      else if (absDistance === 2) {
        displacement = direction * 30; // 30px displacement
      }

      // Apply transform with transition
      card.style.transform = displacement ? `translateX(${displacement}px)` : '';
      card.style.transition = 'transform 0.3s ease';
    });
  }, []);

  const resetNeighborDisplacement = useCallback(() => {
    if (!railRef.current) return;

    // Get all cards in the rail
    const sliderTrack = railRef.current.querySelector('.slick-track');
    if (!sliderTrack) return;

    const cards = sliderTrack.querySelectorAll('.channel-card-container');

    // Reset all transforms
    cards.forEach(card => {
      card.style.transform = '';
    });
  }, []);

  // Handle hover modal show/hide with local state management
  const handleHoverModalShow = useCallback(
    (channel, position, index) => {
      // Clear any existing timeout immediately to prevent race conditions
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      // Adjust position to be relative to CategoryRail container
      const railRect = railRef.current?.getBoundingClientRect();
      const adjustedPosition = railRect
        ? {
            x: position.x - railRect.left,
            y: position.y - railRect.top,
          }
        : position;

      // Show modal using local state (immediate)
      setLocalModalState({
        isVisible: true,
        channel,
        position: adjustedPosition,
        index,
      });

      // Store the hovered card index and apply displacement
      setHoveredCardIndex(index);
      applyNeighborDisplacement(index);
    },
    [applyNeighborDisplacement],
  );

  const handleHoverModalHide = useCallback(() => {
    // Clear any existing timeout to prevent multiple timeouts
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // Hide modal with delay using local state
    hideTimeoutRef.current = setTimeout(() => {
      setLocalModalState({
        isVisible: false,
        channel: null,
        position: null,
        index: -1,
      });
      // Reset neighbor displacement and hovered index
      resetNeighborDisplacement();
      setHoveredCardIndex(-1);
      hideTimeoutRef.current = null; // Clear reference after execution
    }, 300);
  }, [resetNeighborDisplacement]);

  const handleModalMouseEnter = useCallback(() => {
    // Cancel hide timeout and clear reference
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }
  }, []);

  const handleModalMouseLeave = useCallback(() => {
    // Start hide timeout
    handleHoverModalHide();
  }, [handleHoverModalHide]);

  const handleOpenPDPModal = useCallback(
    videoState => {
      // Clear any pending hide timeout first
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }

      // Hide the hover modal first using local state
      setLocalModalState({
        isVisible: false,
        channel: null,
        position: null,
        index: -1,
      });

      // Reset neighbor displacement
      resetNeighborDisplacement();
      setHoveredCardIndex(-1);

      // Open the PDP modal with the current channel
      setPdpModalData({
        isVisible: true,
        channel: localModalState.channel,
        videoState: videoState || { autoPlay: false, currentTime: 0 },
      });
    },
    [localModalState.channel, resetNeighborDisplacement],
  );

  const handleClosePDPModal = useCallback(() => {
    setPdpModalData({
      isVisible: false,
      channel: null,
      videoState: { autoPlay: false, currentTime: 0 },
    });
  }, []);

  // Immediate hide for when card is clicked directly (no delay)
  // This prevents race conditions with video player when PDP modal opens
  const handleHoverModalHideImmediate = useCallback(() => {
    // Clear any pending hide timeout
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = null;
    }

    // Hide immediately without delay
    setLocalModalState({
      isVisible: false,
      channel: null,
      position: null,
      index: -1,
    });
    
    // Reset neighbor displacement
    resetNeighborDisplacement();
    setHoveredCardIndex(-1);
  }, [resetNeighborDisplacement]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (hideTimeoutRef.current) {
        clearTimeout(hideTimeoutRef.current);
        hideTimeoutRef.current = null;
      }
    };
  }, []);

  const handleSeeAllClick = () => {
    if (!category) return;

    // Special case: Continue Watching should go to My Lists continue watching tab
    if (category._id === 'continue-watching' || category.style === 'continue-watching') {
      navigate('/my-lists/continue-watching');
      return;
    }

    // Regular categories - prefer slug, fallback to ID
    const identifier = category.slug || category._id;
    if (identifier) {
      navigate(`/category/${identifier}`);
    }
  };

  // Add null check for category itself
  if (!category) {
    return (
      <div className="my-8 category-rail" ref={railRef}>
        <div className="px-2 text-white/50">Category data unavailable</div>
      </div>
    );
  }

  const title = category.display_name || category.name || 'Untitled Category';

  // Use shared config for slidesToShow
  const getCurrentSlidesToShow = () => {
    const width = window.innerWidth;
    const type = category?.style === 'top-number-poster' ? 'poster' : 'regular';
    return getSlidesToShow(width, type);
  };

  // Calculate if "See All" button should be shown
  const shouldShowSeeAll = () => {
    if (!channels || channels.length === 0) return false;
    const visibleCount = Math.floor(getCurrentSlidesToShow());
    return channels.length > visibleCount;
  };

  return (
    <div
      className={`category-rail relative ${
        channels.length <= Math.floor(getCurrentSlidesToShow()) ? 'rail-insufficient-items' : ''
      }`}
      ref={railRef}
    >
      <div className="flex items-center justify-between mb-4">
        {/* Conditional rendering: Logo if available, otherwise text title */}
        {category.logo ? (
          <div className="category-logo-container">
            <img 
              src={`${category.logo}/300/50`} 
              alt={title}
              className="category-logo h-8 md:h-10 lg:h-12 max-w-[200px] md:max-w-[250px] lg:max-w-[300px] object-contain"
              srcSet={`
                ${category.logo}/120/20 120w,
                ${category.logo}/180/30 180w,
                ${category.logo}/240/40 240w,
                ${category.logo}/300/50 300w,
                ${category.logo}/360/60 360w,
                ${category.logo}/420/70 420w
              `}
              sizes="(max-width: 767px) 120px, (max-width: 1023px) 180px, (max-width: 1366px) 240px, 300px"
              onError={(e) => {
                // Fallback to text if logo fails to load
                e.target.style.display = 'none';
                e.target.nextElementSibling.style.display = 'block';
              }}
            />
            <h2 className="font-bold text-white hidden">{title}</h2>
          </div>
        ) : (
          <h2 className="font-bold text-white">{title}</h2>
        )}
        {shouldShowSeeAll() && (
          <button className="text-sm text-white/70 hover:text-white" onClick={handleSeeAllClick}>
            See All
          </button>
        )}
      </div>

      {error ? (
        <div className="flex flex-col items-center justify-center w-full py-4">
          <div className="mb-2 text-red-400">{error}</div>
          <button
            onClick={retry}
            className="px-4 py-1 text-sm text-white rounded-full bg-accent/80 hover:bg-accent"
          >
            Retry
          </button>
        </div>
      ) : (
        <div>
          <Slider
            dots={false}
            infinite={!hasBeenVisible || loading ? false : channels.length > 5}
            speed={500}
            slidesToShow={getSlidesToShow(
              window.innerWidth,
              category?.style === 'top-number-poster' ? 'poster' : 'regular',
            )}
            slidesToScroll={3}
            arrows={!hasBeenVisible || loading ? false : true}
            responsive={RAIL_BREAKPOINTS.map(bp => ({
              breakpoint: bp.breakpoint,
              settings: {
                slidesToShow: bp[category?.style === 'top-number-poster' ? 'poster' : 'regular'],
                slidesToScroll: 1,
              },
            }))}
          >
            {(() => {
              if (!hasBeenVisible || loading) {
                // Match skeleton count to actual slidesToShow for current breakpoint
                const getSkeletonCount = () => {
                  const width = window.innerWidth;
                  const type = category?.style || 'regular';
                  return Math.ceil(getSlidesToShow(width, type));
                };

                const skeletonItems = Array.from({ length: getSkeletonCount() }).map((_, index) => (
                  <div key={`skeleton-${index}`} className="px-2">
                    <ChannelCardSkeleton isPosterFormat={category?.style === 'top-number-poster'} />
                  </div>
                ));
                return skeletonItems;
              } else {
                return channels.map((channel, index) => (
                  <div key={channel?._id || index} className="px-2">
                    <ChannelCard
                      channel={channel || {}}
                      style={category?.style}
                      index={index}
                      onHoverModalShow={handleHoverModalShow}
                      onHoverModalHide={handleHoverModalHide}
                      onCardClick={handleHoverModalHideImmediate}
                    />
                  </div>
                ));
              }
            })()}
          </Slider>
        </div>
      )}
      {/* Integrated Hover Modal - Only render if this rail's modal is active */}
      {localModalState.isVisible && localModalState.channel && (
        <ThumbnailHoverModal
          channel={localModalState.channel}
          isVisible={true}
          position={localModalState.position}
          onMouseEnter={handleModalMouseEnter}
          onMouseLeave={handleModalMouseLeave}
          onOpenPDPModal={handleOpenPDPModal}
          preCalculatedContinueWatching={preCalculatedContinueWatching}
          preCalculatedWatchAgain={preCalculatedWatchAgain}
        />
      )}
      {/* PDP Modal */}
      {pdpModalData.isVisible && (
        <PDPModal
          channel={pdpModalData.channel}
          isVisible={pdpModalData.isVisible}
          onClose={handleClosePDPModal}
          autoPlayVideo={pdpModalData.videoState.autoPlay}
          videoCurrentTime={pdpModalData.videoState.currentTime}
        />
      )}
    </div>
  );
});

export default CategoryRail;
